import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Link } from 'react-router-dom';
import { Shield, Users, FileText, Zap, TrendingUp, CheckCircle } from 'lucide-react';

const ForPEOs = () => {
  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>For PEOs - Partner with The PEO App | HIPAA & SOC II Compliant Platform</title>
        <meta name="description" content="Join our vetted network of 523+ PEO providers or use our HIPAA & SOC II compliant platform as a SaaS solution for your client acquisition and document management." />
        <meta name="keywords" content="PEO software, PEO platform, PEO vendor network, HIPAA compliant, SOC II, PEO SaaS" />
      </Helmet>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary/10 via-background to-secondary/10 py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Badge variant="secondary" className="mb-4">
              HIPAA & SOC II Compliant Platform
            </Badge>
            <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-6">
              Grow Your PEO Business with <span className="text-primary">The PEO App</span>
            </h1>
            <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
              Choose your path: Join our vetted network of 523+ PEO providers to access qualified leads, 
              or use our secure, compliant platform as a SaaS solution for your own client acquisition.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" asChild>
                <Link to="/peo/apply">Apply to Join Network</Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link to="/signup">Try SaaS Platform</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Two Paths Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-foreground mb-4">Two Ways to Grow Your Business</h2>
            <p className="text-lg text-muted-foreground">Choose the path that best fits your business model</p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {/* Vendor Path */}
            <Card className="border-2 hover:border-primary/50 transition-colors">
              <CardHeader>
                <div className="flex items-center gap-3 mb-2">
                  <Users className="h-8 w-8 text-primary" />
                  <CardTitle className="text-2xl">Join as Vendor Partner</CardTitle>
                </div>
                <CardDescription className="text-base">
                  Get vetted to join our network and access qualified PEO shoppers
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                    <span>Access to 1,000+ qualified leads monthly</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                    <span>Pre-qualified prospects with documented needs</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                    <span>Reduce marketing costs by 60-80%</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                    <span>Join network of 523 vetted PEO providers</span>
                  </li>
                </ul>
                <Button className="w-full" asChild>
                  <Link to="/peo/apply">Start Vetting Process</Link>
                </Button>
              </CardContent>
            </Card>

            {/* SaaS Path */}
            <Card className="border-2 hover:border-primary/50 transition-colors">
              <CardHeader>
                <div className="flex items-center gap-3 mb-2">
                  <Zap className="h-8 w-8 text-primary" />
                  <CardTitle className="text-2xl">Use as SaaS Platform</CardTitle>
                </div>
                <CardDescription className="text-base">
                  White-label our platform for your own client acquisition
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                    <span>HIPAA & SOC II compliant infrastructure</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                    <span>Secure document collection & management</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                    <span>Streamlined client onboarding process</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                    <span>Maintain direct client relationships</span>
                  </li>
                </ul>
                <Button className="w-full" variant="outline" asChild>
                  <Link to="/signup">Start Free Trial</Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Security & Compliance */}
      <section className="py-16 bg-muted/50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Shield className="h-16 w-16 text-primary mx-auto mb-6" />
            <h2 className="text-3xl font-bold text-foreground mb-4">Enterprise-Grade Security</h2>
            <p className="text-lg text-muted-foreground mb-8">
              Built for PEOs handling sensitive employee data and healthcare information
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <Badge variant="outline" className="text-lg py-2 px-4">HIPAA Compliant</Badge>
                <p className="text-sm text-muted-foreground mt-2">Healthcare data protection</p>
              </div>
              <div className="text-center">
                <Badge variant="outline" className="text-lg py-2 px-4">SOC II Certified</Badge>
                <p className="text-sm text-muted-foreground mt-2">Enterprise security standards</p>
              </div>
              <div className="text-center">
                <Badge variant="outline" className="text-lg py-2 px-4">Bank-Level Encryption</Badge>
                <p className="text-sm text-muted-foreground mt-2">256-bit SSL encryption</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-foreground mb-4">Why PEOs Choose The PEO App</h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="text-center">
              <TrendingUp className="h-12 w-12 text-primary mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Increased Revenue</h3>
              <p className="text-muted-foreground">
                PEOs in our network see 40% more qualified leads compared to traditional marketing
              </p>
            </div>
            
            <div className="text-center">
              <FileText className="h-12 w-12 text-primary mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Streamlined Process</h3>
              <p className="text-muted-foreground">
                Reduce client onboarding time by 70% with our automated document collection
              </p>
            </div>
            
            <div className="text-center">
              <Shield className="h-12 w-12 text-primary mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Risk Mitigation</h3>
              <p className="text-muted-foreground">
                Enterprise-grade security reduces compliance risk and builds client trust
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-primary/5">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-foreground mb-4">Ready to Scale Your PEO Business?</h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            Join the growing network of successful PEO providers using The PEO App to grow their business
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" asChild>
              <Link to="/peo/apply">Apply for Vendor Network</Link>
            </Button>
            <Button size="lg" variant="outline" asChild>
              <Link to="/signup">Try SaaS Platform Free</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ForPEOs;